import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Braces,
  Check,
  Copy,
  Download,
  Eye,
  FileText,
  FileImage,
  FileUp,
  ImagePlus,
  Heading1,
  Layers,
  LayoutTemplate,
  Lock,
  Minus,
  MousePointer2,
  Plus,
  Redo2,
  Save,
  Search,
  Settings2,
  Trash2,
  Type,
  Undo2,
  Unlock,
  X,
} from 'lucide-react'
import { useNotification } from '@context/NotificationContext'
import Modal from '@components/shared/Modal'
import LegalTemplates from '@services/legalTemplates'
import { APP_CONFIG } from '@config/constants'
import TemplateCanvas from './components/TemplateCanvas'
import TemplateImportDialog from './components/TemplateImportDialog'
import {
  createDocument,
  createElement,
  createPage,
  duplicateElement,
  FONTS,
  VARIABLES,
  normalizeDocument,
  resolveText,
} from './templateDocument'
import { getLayerLabel, getLayerName, getLayerRole, LAYER_ROLES } from './templateLayers'
import { loadTemplateFonts } from './templateFonts'
import './templateFonts.css'
import './legalTemplates.css'

const LAYER_ICONS = {
  text: FileText,
  title: Heading1,
  field: Braces,
  header: FileText,
  footer: FileText,
  image: ImagePlus,
  background: Layers,
  page: FileImage,
  line: Minus,
}
const layerDetail = (element) => {
  const role = getLayerRole(element)
  const content = element.type === 'text'
    ? element.text?.replace(/\s+/g, ' ').trim().slice(0, 100) || 'Texto vazio'
    : role === 'background'
      ? 'Elementos gráficos da página'
      : role === 'page'
        ? 'Conteúdo preservado como imagem'
        : role === 'image' ? 'Imagem do documento' : 'Separador'
  return element.layerName?.trim() ? `${getLayerLabel(element)} · ${content}` : content
}
const emptyModel = () => ({
  name: 'Novo modelo',
  description: '',
  area: 'civel',
  document: createDocument(),
})
const fallbackAreas = Object.entries(APP_CONFIG.AREAS).map(([key, label]) => ({
  key,
  label,
}))

function Field({ label, children, className = '' }) {
  return (
    <label className={`lt-field ${className}`}>
      <span>{label}</span>
      {children}
    </label>
  )
}

export default function LegalTemplatesPage() {
  const { showError, showSuccess } = useNotification()
  const [models, setModels] = useState([])
  const [areas, setAreas] = useState(fallbackAreas)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [model, setModel] = useState(null)
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [importing, setImporting] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [activePageId, setActivePageId] = useState(null)
  const [zoom, setZoom] = useState(window.innerWidth < 1450 ? 0.65 : 0.75)
  const [preview, setPreview] = useState(false)
  const [guides, setGuides] = useState(true)
  const [tab, setTab] = useState('elements')
  const [search, setSearch] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [values, setValues] = useState({})
  const [history, setHistory] = useState({ past: [], future: [] })
  const [pendingExit, setPendingExit] = useState(null)
  const uploadRef = useRef(null)
  const modelRef = useRef(model)
  modelRef.current = model
  const dirty = model && JSON.stringify(model) !== saved
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Boolean(dirty) && currentLocation.pathname !== nextLocation.pathname,
  )
  const selectedPage = model?.document.pages.find((page) =>
    page.elements.some((element) => element.id === selectedId),
  )
  const selected = selectedPage?.elements.find(
    (element) => element.id === selectedId,
  )
  const activePage =
    selectedPage ||
    model?.document.pages.find((page) => page.id === activePageId) ||
    model?.document.pages[0]
  const areaName = (key) => areas.find((area) => area.key === key)?.label || key
  const filteredModels = models.filter(
    (item) =>
      (!areaFilter || item.area === areaFilter) &&
      `${item.name} ${item.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  )

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    const [result, areaResult] = await Promise.all([
      LegalTemplates.list(),
      LegalTemplates.areas(),
    ])
    if (result.success) setModels(result.data || [])
    else setLoadError(result.error || 'Não foi possível carregar os modelos.')
    if (areaResult.success && areaResult.data?.length) setAreas(areaResult.data)
    setLoading(false)
  }, [])
  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!dirty) return undefined
    const beforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    // Logout changes authentication before routing; defer its callback as well.
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [dirty])

  const commit = useCallback((next) => {
    const current = modelRef.current
    if (!current || next === current) return
    setHistory((state) => ({
      past: [...state.past.slice(-39), current],
      future: [],
    }))
    setModel(next)
  }, [])
  const changeDocument = (document) => commit({ ...modelRef.current, document })
  const patchElement = (patch) => {
    if (!selected) return
    changeDocument({
      ...model.document,
      pages: model.document.pages.map((page) => ({
        ...page,
        elements: page.elements.map((element) =>
          element.id === selected.id ? { ...element, ...patch } : element,
        ),
      })),
    })
  }
  const undo = () => {
    if (!history.past.length) return
    setModel(history.past.at(-1))
    setHistory({
      past: history.past.slice(0, -1),
      future: [model, ...history.future],
    })
  }
  const redo = () => {
    if (!history.future.length) return
    setModel(history.future[0])
    setHistory({
      past: [...history.past, model],
      future: history.future.slice(1),
    })
  }
  const enterEditor = (next, isSaved = false) => {
    const normalized = { ...next, document: normalizeDocument(next.document) }
    setModel(normalized)
    setSaved(isSaved ? JSON.stringify(normalized) : '')
    setSelectedId(null)
    setActivePageId(normalized.document.pages[0].id)
    setPreview(false)
    setHistory({ past: [], future: [] })
    setValues({})
  }
  const open = async (item) => {
    setBusy(true)
    const result = await LegalTemplates.get(item.id)
    if (result.success) enterEditor(result.data, true)
    else showError(result.error)
    setBusy(false)
  }
  const save = async () => {
    if (!model.name.trim())
      return showError('Dê um nome ao modelo antes de salvar.')
    setBusy(true)
    const snapshot = model
    const result = await LegalTemplates.save(snapshot)
    if (result.success) {
      // Keep edits made while the request was running; only the submitted snapshot was saved.
      const persisted = result.data
      setSaved(JSON.stringify(persisted))
      setModel((current) =>
        !current
          ? null
          : current === snapshot
            ? persisted
            : {
                ...current,
                id: persisted.id,
                version: persisted.version,
                updatedAt: persisted.updatedAt,
                createdAt: persisted.createdAt,
              },
      )
      setHistory({ past: [], future: [] })
      showSuccess('Modelo salvo no sistema.')
      await load()
    } else
      showError(
        result.error ||
          'Não foi possível salvar. Suas alterações continuam no editor.',
      )
    setBusy(false)
  }
  const discardEditor = () => {
    setModel(null)
    modelRef.current = null
    setSaved('')
    setHistory({ past: [], future: [] })
    setValues({})
    setPreview(false)
    setActivePageId(null)
    setSelectedId(null)
  }
  const leave = () => {
    if (dirty) setPendingExit({ proceed: null })
    else discardEditor()
  }
  const cancelExit = () => {
    setPendingExit(null)
    if (navigationBlocker.state === 'blocked') navigationBlocker.reset()
  }
  const confirmExit = () => {
    const proceed = pendingExit?.proceed
    setPendingExit(null)
    discardEditor()
    if (navigationBlocker.state === 'blocked') navigationBlocker.proceed()
    proceed?.()
  }
  const addElement = (type, overrides = {}) => {
    if (!activePage) return
    if (activePage.elements.length >= 100)
      return showError('Esta página atingiu o limite de 100 elementos.')
    const element = createElement(type, overrides)
    changeDocument({
      ...model.document,
      pages: model.document.pages.map((page) =>
        page.id === activePage.id
          ? { ...page, elements: [...page.elements, element] }
          : page,
      ),
    })
    setSelectedId(element.id)
    setPreview(false)
  }
  const removeElement = () => {
    if (!selected || selected.locked) return
    changeDocument({
      ...model.document,
      pages: model.document.pages.map((page) => ({
        ...page,
        elements: page.elements.filter((element) => element.id !== selected.id),
      })),
    })
    setSelectedId(null)
  }
  const handleEditorKeyDown = (event) => {
    if (
      event.key !== 'Delete' ||
      event.defaultPrevented ||
      event.nativeEvent.isComposing ||
      event.ctrlKey || event.metaKey || event.altKey ||
      preview || !selected || selected.locked ||
      pendingExit || navigationBlocker.state === 'blocked' ||
      !event.currentTarget.contains(event.target) ||
      event.target.isContentEditable ||
      event.target.closest('input, textarea, select, [role="textbox"], dialog, [role="dialog"]')
    ) return
    event.preventDefault()
    event.stopPropagation()
    removeElement()
  }
  const duplicate = () => {
    if (!selected) return
    addElement(selected.type, duplicateElement(selected, { locked: false }))
  }
  const orderElement = (direction) => {
    const elements = [...selectedPage.elements]
    const index = elements.findIndex((element) => element.id === selectedId)
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= elements.length) return
    ;[elements[index], elements[nextIndex]] = [
      elements[nextIndex],
      elements[index],
    ]
    changeDocument({
      ...model.document,
      pages: model.document.pages.map((page) =>
        page.id === selectedPage.id ? { ...page, elements } : page,
      ),
    })
  }
  const addPage = () => {
    if (model.document.pages.length >= 30)
      return showError('O modelo pode ter até 30 páginas.')
    const page = createPage()
    changeDocument({
      ...model.document,
      pages: [...model.document.pages, page],
    })
    setActivePageId(page.id)
    setSelectedId(null)
  }
  const removePage = (page) => {
    if (model.document.pages.length <= 1) return
    if (
      page.elements.length &&
      !window.confirm('Excluir esta página e seus elementos?')
    )
      return
    changeDocument({
      ...model.document,
      pages: model.document.pages.filter((item) => item.id !== page.id),
    })
    setSelectedId(null)
    setActivePageId(null)
  }
  const upload = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (
      !['image/png', 'image/jpeg'].includes(file.type) ||
      file.size > 2 * 1024 * 1024
    )
      return showError('Escolha uma imagem PNG ou JPG de até 2 MB.')
    try {
      const imageData = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const image = new Image()
      image.src = imageData
      await image.decode()
      const width = Math.min(70, image.naturalWidth * 0.264583)
      const height = Math.min(
        80,
        (width * image.naturalHeight) / image.naturalWidth,
      )
      addElement('image', {
        imageData,
        width: Math.max(5, width),
        height: Math.max(5, height),
      })
    } catch {
      showError('Não foi possível ler esta imagem.')
    }
  }
  const previewValues = Object.fromEntries(
    VARIABLES.map((variable) => [
      variable.key,
      values[variable.key] ?? variable.example,
    ]),
  )
  const prepareExport = async () => {
    try {
      await loadTemplateFonts(model.document.pages.flatMap((page) => page.elements))
      await document.fonts.ready
    } catch (error) {
      showError(error.message)
      return false
    }
    try {
      await Promise.all(
        [...document.querySelectorAll('.lt-canvas img')].map((image) =>
          image.decode(),
        ),
      )
    } catch {
      showError('Uma imagem não pôde ser carregada para a exportação.')
      return false
    }
    const overflowing = [
      ...document.querySelectorAll('.lt-element-text-content'),
    ].some(
      (element) =>
        element.scrollHeight > element.clientHeight + 1 ||
        element.scrollWidth > element.clientWidth + 1,
    )
    if (overflowing) {
      showError(
        'Há texto que excede a caixa. Aumente sua altura ou distribua o conteúdo em outra página antes de exportar.',
      )
      return false
    }
    return true
  }
  const exportWord = async () => {
    if (!model.name.trim()) return showError('Informe o nome do modelo.')
    if (!(await prepareExport())) return
    setBusy(true)
    const document = preview
      ? {
          ...model.document,
          pages: model.document.pages.map((page) => ({
            ...page,
            elements: page.elements.map((element) => ({
              ...element,
              text: resolveText(element.text || '', previewValues),
            })),
          })),
        }
      : model.document
    const result = await LegalTemplates.word({ ...model, document })
    if (result.success) {
      const url = URL.createObjectURL(result.data)
      const link = window.document.createElement('a')
      link.href = url
      link.download = `${model.name.replace(/[<>:"/\\|?*]/g, '-').slice(0, 100)}.docx`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } else showError(result.error || 'Não foi possível exportar o Word.')
    setBusy(false)
  }
  const print = async () => {
    if (!(await prepareExport())) return
    const title = document.title
    document.title = model.name || 'Modelo de peça'
    window.print()
    document.title = title
  }
  const removeModel = async (item) => {
    if (!window.confirm(`Excluir o modelo “${item.name}”?`)) return
    setBusy(true)
    const result = await LegalTemplates.remove(item)
    if (result.success) {
      showSuccess('Modelo excluído.')
      await load()
    } else showError(result.error)
    setBusy(false)
  }
  const copyModel = async (item) => {
    setBusy(true)
    const result = await LegalTemplates.get(item.id)
    if (result.success)
      enterEditor({
        ...result.data,
        id: undefined,
        version: undefined,
        name: `${result.data.name.slice(0, 152)} — cópia`,
      })
    else showError(result.error)
    setBusy(false)
  }

  if (!model)
    return (
      <section className="lt-library">
        {importing && (
          <TemplateImportDialog
            areas={areas}
            onClose={() => setImporting(false)}
            onImport={(imported) => {
              setImporting(false)
              enterEditor(imported)
            }}
          />
        )}
        <div className="lt-library-intro">
          <div>
            <span className="lt-eyebrow">BIBLIOTECA DO ESCRITÓRIO</span>
            <h2>Uma identidade para cada peça.</h2>
            <p>
              Monte o documento, posicione seus elementos e reutilize seus
              modelos por área jurídica.
            </p>
          </div>
          <div className="lt-library-actions">
            <button
              className="lt-button"
              disabled={busy}
              onClick={() => setImporting(true)}
            >
              <FileUp size={17} /> Importar DOCX / PDF
            </button>
            <button
              className="lt-button lt-primary"
              onClick={() => enterEditor(emptyModel())}
              disabled={busy}
            >
              <Plus size={17} /> Criar modelo
            </button>
          </div>
        </div>
        <div className="lt-library-tools">
          <label className="lt-search">
            <Search size={17} />
            <input
              aria-label="Buscar modelos"
              placeholder="Buscar por nome ou descrição…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <select
            aria-label="Filtrar por área jurídica"
            value={areaFilter}
            onChange={(event) => setAreaFilter(event.target.value)}
          >
            <option value="">Todas as áreas</option>
            {areas.map((area) => (
              <option key={area.key} value={area.key}>
                {area.label}
              </option>
            ))}
          </select>
          <span>
            {models.length} modelo{models.length === 1 ? '' : 's'}
          </span>
        </div>
        {loadError ? (
          <div className="lt-message" role="alert">
            <p>{loadError}</p>
            <button className="lt-button" onClick={load}>
              Tentar novamente
            </button>
          </div>
        ) : loading ? (
          <div className="lt-empty" role="status">
            Carregando modelos…
          </div>
        ) : models.length === 0 ? (
          <div className="lt-empty">
            <LayoutTemplate size={40} strokeWidth={1.2} />
            <h3>Seu primeiro modelo começa aqui</h3>
            <p>
              Adicione o timbre, organize o texto e deixe a estrutura pronta
              para a próxima peça.
            </p>
            <button
              className="lt-button lt-primary"
              onClick={() => enterEditor(emptyModel())}
              disabled={busy}
            >
              Criar meu primeiro modelo <Plus size={16} />
            </button>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="lt-empty">
            <Search size={32} />
            <h3>Nenhum modelo encontrado</h3>
            <p>Ajuste a busca ou selecione outra área jurídica.</p>
          </div>
        ) : (
          <div className="lt-model-grid">
            {filteredModels.map((item) => (
              <article className="lt-model-card" key={item.id}>
                <button
                  className="lt-model-cover"
                  onClick={() => open(item)}
                  disabled={busy}
                  aria-label={`Editar ${item.name}`}
                >
                  <div className="lt-mini-document">
                    <div className="lt-mini-brand" />
                    <div className="lt-mini-heading" />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <div className="lt-mini-signature" />
                  </div>
                  <span>{item.pageCount || 1} pág.</span>
                </button>
                <div className="lt-model-info">
                  <span className="lt-area-badge">{areaName(item.area)}</span>
                  <h3>
                    <button onClick={() => open(item)} disabled={busy}>
                      {item.name}
                    </button>
                  </h3>
                  <p>{item.description || 'Modelo de peça judicial'}</p>
                  <footer>
                    <small>
                      {item.updatedAt
                        ? `Atualizado em ${new Date(item.updatedAt).toLocaleDateString('pt-BR')}`
                        : 'Salvo no escritório'}
                    </small>
                    <button
                      title="Duplicar modelo"
                      aria-label={`Duplicar ${item.name}`}
                      onClick={() => copyModel(item)}
                      disabled={busy}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      title="Excluir modelo"
                      aria-label={`Excluir ${item.name}`}
                      onClick={() => removeModel(item)}
                      disabled={busy}
                    >
                      <Trash2 size={16} />
                    </button>
                  </footer>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    )

  return (
    <section
      className={`lt-editor ${preview ? 'lt-preview-mode' : ''}`}
      onKeyDown={handleEditorKeyDown}
    >
      <Modal
        isOpen={Boolean(pendingExit) || navigationBlocker.state === 'blocked'}
        onClose={cancelExit}
        title="Sair sem salvar?"
        icon="fa-exclamation-triangle"
        size="small"
        footer={
          <>
            <button className="btn btn-outline" onClick={cancelExit} autoFocus>
              Continuar editando
            </button>
            <button className="btn btn-danger" onClick={confirmExit}>
              Sair sem salvar
            </button>
          </>
        }
      >
        <p>Há alterações não salvas neste modelo. Se sair agora, essas alterações serão descartadas.</p>
      </Modal>
      <header className="lt-topbar">
        <button
          className="lt-icon-button"
          aria-label="Voltar aos modelos"
          onClick={leave}
        >
          <ArrowLeft size={19} />
        </button>
        <div className="lt-model-title">
          <input
            aria-label="Nome do modelo"
            maxLength={160}
            value={model.name}
            onChange={(event) => commit({ ...model, name: event.target.value })}
          />
          <span>
            {dirty ? (
              <>
                <span className="lt-unsaved-dot" />
                {model.id ? 'Alterações não salvas' : 'Rascunho · ainda não salvo'}
              </>
            ) : (
              <>
                <Check size={12} /> Salvo no sistema
              </>
            )}
          </span>
        </div>
        <div className="lt-mode-switch">
          <button
            className={!preview ? 'is-active' : ''}
            onClick={() => setPreview(false)}
          >
            <MousePointer2 size={15} /> Editar
          </button>
          <button
            className={preview ? 'is-active' : ''}
            onClick={() => setPreview(true)}
          >
            <Eye size={15} /> Prévia
          </button>
        </div>
        <div className="lt-topbar-actions">
          <button className="lt-button" disabled={busy} onClick={exportWord}>
            <Download size={15} /> Word
          </button>
          <button className="lt-button" onClick={print}>
            <FileText size={15} /> PDF / Imprimir
          </button>
          <button
            className="lt-button lt-primary"
            onClick={save}
            disabled={busy || !dirty}
          >
            <Save size={15} /> {busy ? 'Aguarde…' : 'Salvar'}
          </button>
        </div>
      </header>
      <div className="lt-toolbar">
        <div>
          <button
            className="lt-icon-button"
            onClick={undo}
            disabled={!history.past.length || preview}
            title="Desfazer"
            aria-label="Desfazer"
          >
            <Undo2 size={17} />
          </button>
          <button
            className="lt-icon-button"
            onClick={redo}
            disabled={!history.future.length || preview}
            title="Refazer"
            aria-label="Refazer"
          >
            <Redo2 size={17} />
          </button>
          <span className="lt-divider" />
          <span className="lt-toolbar-caption">
            A4 <span>210 × 297 mm</span>
          </span>
        </div>
        <div>
          <label className="lt-guide-toggle">
            <input
              type="checkbox"
              checked={guides}
              onChange={(event) => setGuides(event.target.checked)}
              disabled={preview}
            />{' '}
            Guias
          </label>
          <span className="lt-divider" />
          <button
            className="lt-icon-button"
            onClick={() => setZoom((value) => Math.max(0.35, value - 0.1))}
            aria-label="Diminuir zoom"
          >
            <Minus size={16} />
          </button>
          <select
            aria-label="Zoom"
            value={Math.round(zoom * 100)}
            onChange={(event) => setZoom(Number(event.target.value) / 100)}
          >
            {[35, 50, 65, 75, 85, 100, 125, 150, Math.round(zoom * 100)]
              .filter((value, index, array) => array.indexOf(value) === index)
              .sort((a, b) => a - b)
              .map((value) => (
                <option key={value} value={value}>
                  {value}%
                </option>
              ))}
          </select>
          <button
            className="lt-icon-button"
            onClick={() => setZoom((value) => Math.min(1.5, value + 0.1))}
            aria-label="Aumentar zoom"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
      <div className="lt-workspace">
        <aside className="lt-left-panel">
          <div className="lt-panel-tabs">
            <button
              className={tab === 'elements' ? 'is-active' : ''}
              onClick={() => setTab('elements')}
            >
              <Plus size={15} /> Elementos
            </button>
            <button
              className={tab === 'layers' ? 'is-active' : ''}
              onClick={() => setTab('layers')}
            >
              <Layers size={15} /> Camadas
            </button>
          </div>
          {preview ? (
            <div className="lt-panel-section">
              <h3>Dados da prévia</h3>
              <p className="lt-hint">
                Exemplos para visualizar e exportar. O modelo salvo preserva os
                campos.
              </p>
              {VARIABLES.map((variable) => (
                <Field label={variable.label} key={variable.key}>
                  <input
                    value={values[variable.key] ?? variable.example}
                    onChange={(event) =>
                      setValues({
                        ...values,
                        [variable.key]: event.target.value,
                      })
                    }
                  />
                </Field>
              ))}
            </div>
          ) : tab === 'elements' ? (
            <>
              <div className="lt-panel-section">
                <h3>Insira na página</h3>
                <div className="lt-element-palette">
                  <button
                    onClick={() =>
                      addElement('text', {
                        layerRole: 'title',
                        text: 'TÍTULO DA PEÇA',
                        fontSize: 16,
                        bold: true,
                        height: 15,
                      })
                    }
                  >
                    <Type size={25} />
                    <span>Título</span>
                  </button>
                  <button onClick={() => addElement('text', { layerRole: 'text' })}>
                    <FileText size={24} />
                    <span>Texto</span>
                  </button>
                  <button onClick={() => uploadRef.current?.click()}>
                    <ImagePlus size={24} />
                    <span>Imagem / logo</span>
                  </button>
                  <button onClick={() => addElement('line')}>
                    <Minus size={25} />
                    <span>Divisória</span>
                  </button>
                </div>
                <input
                  ref={uploadRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  hidden
                  onChange={upload}
                />
                <p className="lt-hint">
                  Arraste para posicionar. Use o canto inferior para
                  redimensionar.
                </p>
              </div>
              <div className="lt-panel-section">
                <h3>Campos do documento</h3>
                <p className="lt-hint">
                  Reutilize a estrutura sem fixar os dados de um processo.
                </p>
                <div className="lt-variable-list">
                  {VARIABLES.map((variable) => (
                    <button
                      key={variable.key}
                      onClick={() =>
                        addElement('text', {
                          layerRole: 'field',
                          text: `{{${variable.key}}}`,
                          height: 12,
                        })
                      }
                    >
                      <span>{variable.label}</span>
                      <Plus size={13} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="lt-panel-section">
                <h3>Assinatura</h3>
                <button
                  className="lt-button lt-full"
                  onClick={() =>
                    addElement('text', {
                      text: '{{advogado_nome}}\nOAB {{advogado_oab}}',
                      alignment: 'center',
                      width: 130,
                      height: 22,
                      x: 40,
                      y: 240,
                    })
                  }
                >
                  Inserir assinatura <Plus size={14} />
                </button>
              </div>
            </>
          ) : (
            <div className="lt-panel-section">
              <h3>Elementos da página</h3>
              <p className="lt-hint">As camadas no topo aparecem à frente. Selecione uma para ajustar o nome e a classificação.</p>
              <div className="lt-layer-list">
                {[...(activePage?.elements || [])].reverse().map((element) => {
                  const Icon = LAYER_ICONS[getLayerRole(element)]
                  const name = getLayerName(element)
                  const detail = layerDetail(element)
                  return (
                  <button
                    key={element.id}
                    className={selectedId === element.id ? 'is-active' : ''}
                    data-layer-id={element.id}
                    data-layer-role={getLayerRole(element)}
                    aria-pressed={selectedId === element.id}
                    aria-label={`Selecionar camada: ${name} — ${detail}${element.locked ? ' (bloqueada)' : ''}`}
                    title={`${name} — ${detail}`}
                    onClick={() => setSelectedId(element.id)}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span className="lt-layer-info">
                      <strong>{name}</strong>
                      <small>{detail}</small>
                    </span>
                    {element.locked && <Lock size={12} aria-hidden="true" />}
                  </button>
                  )
                })}
                {!activePage?.elements.length && (
                  <p className="lt-hint">Esta página ainda está em branco.</p>
                )}
              </div>
            </div>
          )}
          <div className="lt-panel-section lt-pages-panel">
            <h3>
              Páginas{' '}
              <button
                className="lt-icon-button"
                onClick={addPage}
                disabled={preview}
                aria-label="Adicionar página"
              >
                <Plus size={15} />
              </button>
            </h3>
            {model.document.pages.map((page, index) => (
              <div
                className={`lt-page-item ${activePage?.id === page.id ? 'is-active' : ''}`}
                key={page.id}
              >
                <button
                  onClick={() => {
                    setActivePageId(page.id)
                    setSelectedId(null)
                    document
                      .querySelector(`[data-page-id="${page.id}"]`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                  }}
                >
                  <FileText size={15} /> Página {index + 1}
                  <small>{page.elements.length}</small>
                </button>
                {!preview && model.document.pages.length > 1 && (
                  <button
                    aria-label={`Excluir página ${index + 1}`}
                    onClick={() => removePage(page)}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </aside>
        <div className="lt-center">
          <TemplateCanvas
            document={model.document}
            onChange={changeDocument}
            selectedId={preview ? null : selectedId}
            onSelect={setSelectedId}
            onPageSelect={setActivePageId}
            zoom={zoom}
            preview={preview}
            showGuides={guides && !preview}
            values={preview ? previewValues : {}}
          />
          <div className="lt-canvas-footer">
            {model.document.pages.length} página
            {model.document.pages.length === 1 ? '' : 's'} <span>•</span>{' '}
            {areaName(model.area)} <span>•</span>{' '}
            {preview ? 'Prévia do documento' : 'Edição do modelo'}
          </div>
        </div>
        <aside className="lt-right-panel">
          <div className="lt-panel-heading">
            <Settings2 size={16} />
            <h3>
              {selected && !preview
                ? 'Propriedades'
                : 'Configurações do modelo'}
            </h3>
            {selected && !preview && (
              <button
                className="lt-icon-button"
                onClick={() => setSelectedId(null)}
                aria-label="Fechar propriedades"
              >
                <X size={15} />
              </button>
            )}
          </div>
          {selected && !preview ? (
            <>
              <div className="lt-panel-section">
                <h3>
                  {getLayerLabel(selected)}
                </h3>
                <div className="lt-selected-actions">
                  <button
                    className="lt-icon-button"
                    title="Duplicar elemento"
                    aria-label="Duplicar elemento"
                    onClick={duplicate}
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    className="lt-icon-button"
                    title="Trazer para frente"
                    aria-label="Trazer para frente"
                    onClick={() => orderElement(1)}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    className="lt-icon-button"
                    title="Enviar para trás"
                    aria-label="Enviar para trás"
                    onClick={() => orderElement(-1)}
                  >
                    <ArrowDown size={16} />
                  </button>
                  <button
                    className="lt-icon-button"
                    title={selected.locked ? 'Desbloquear' : 'Bloquear'}
                    aria-label={
                      selected.locked
                        ? 'Desbloquear elemento'
                        : 'Bloquear elemento'
                    }
                    onClick={() => patchElement({ locked: !selected.locked })}
                  >
                    {selected.locked ? (
                      <Lock size={16} />
                    ) : (
                      <Unlock size={16} />
                    )}
                  </button>
                  <button
                    className="lt-icon-button lt-danger"
                    aria-label="Excluir elemento"
                    aria-keyshortcuts="Delete"
                    title="Excluir elemento (Delete)"
                    disabled={selected.locked}
                    onClick={removeElement}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="lt-panel-section">
                <Field label="Nome da camada">
                  <input
                    value={selected.layerName || ''}
                    maxLength={100}
                    placeholder={getLayerLabel(selected)}
                    onChange={(event) => patchElement({
                      layerName: event.target.value.replace(/[<>\p{Cc}]/gu, ''),
                    })}
                  />
                </Field>
                <Field label="Tipo da camada">
                  <select
                    value={getLayerRole(selected)}
                    onChange={(event) => patchElement({ layerRole: event.target.value })}
                  >
                    {LAYER_ROLES[selected.type].map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <fieldset
                disabled={selected.locked}
                className="lt-properties-fields"
              >
                <div className="lt-panel-section">
                  <h3>
                    Posição e tamanho <small>mm</small>
                  </h3>
                  <div className="lt-field-grid">
                    {[
                      ['x', 'X', 0, 210 - selected.width],
                      ['y', 'Y', 0, 297 - selected.height],
                      ['width', 'Largura', 1, 210 - selected.x],
                      ['height', 'Altura', 1, 297 - selected.y],
                    ].map(([key, label, min, max]) => (
                      <Field key={key} label={label}>
                        <input
                          type="number"
                          step="0.5"
                          min={min}
                          max={max}
                          value={Math.round(selected[key] * 10) / 10}
                          onChange={(event) => {
                            if (event.target.value !== '')
                              patchElement({
                                [key]: Math.min(
                                  max,
                                  Math.max(min, Number(event.target.value)),
                                ),
                              })
                          }}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
                {selected.type === 'text' && (
                  <div className="lt-panel-section">
                    <h3>Conteúdo e tipografia</h3>
                    <Field label="Conteúdo">
                      <textarea
                        aria-label="Conteúdo do elemento"
                        rows={7}
                        maxLength={20000}
                        value={selected.text || ''}
                        onChange={(event) =>
                          patchElement({ text: event.target.value })
                        }
                      />
                    </Field>
                    <Field label="Fonte">
                      <select
                        value={selected.fontFamily}
                        onChange={(event) =>
                          patchElement({ fontFamily: event.target.value })
                        }
                      >
                        {FONTS.map((font) => (
                          <option key={font} value={font}>
                            {font}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <div className="lt-field-grid">
                      <Field label="Tamanho (pt)">
                        <input
                          type="number"
                          min="6"
                          max="72"
                          value={selected.fontSize}
                          onChange={(event) =>
                            patchElement({
                              fontSize: Math.max(
                                6,
                                Math.min(72, Number(event.target.value) || 12),
                              ),
                            })
                          }
                        />
                      </Field>
                      <Field label="Entrelinhas">
                        <select
                          value={selected.lineHeight}
                          onChange={(event) =>
                            patchElement({
                              lineHeight: Number(event.target.value),
                            })
                          }
                        >
                          {[1, 1.15, 1.5, 2].map((value) => (
                            <option key={value}>{value}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="lt-type-buttons">
                      {[
                        ['bold', 'N'],
                        ['italic', 'I'],
                        ['underline', 'S'],
                      ].map(([key, label]) => (
                        <button
                          className={selected[key] ? 'is-active' : ''}
                          key={key}
                          aria-label={
                            {
                              bold: 'Negrito',
                              italic: 'Itálico',
                              underline: 'Sublinhado',
                            }[key]
                          }
                          aria-pressed={selected[key]}
                          onClick={() =>
                            patchElement({ [key]: !selected[key] })
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <Field label="Alinhamento">
                      <select
                        value={selected.alignment}
                        onChange={(event) =>
                          patchElement({ alignment: event.target.value })
                        }
                      >
                        <option value="left">À esquerda</option>
                        <option value="center">Centralizado</option>
                        <option value="right">À direita</option>
                        <option value="justify">Justificado</option>
                      </select>
                    </Field>
                  </div>
                )}
                {selected.type !== 'image' && (
                  <div className="lt-panel-section">
                    <Field label="Cor">
                      <div className="lt-color-field">
                        <input
                          type="color"
                          value={selected.color}
                          onChange={(event) =>
                            patchElement({ color: event.target.value })
                          }
                        />
                        <span>{selected.color}</span>
                      </div>
                    </Field>
                  </div>
                )}
              </fieldset>
              {selected.locked && (
                <p className="lt-hint lt-panel-section">
                  Desbloqueie para alterar o conteúdo, a posição ou o tamanho. O nome e o tipo da camada podem ser ajustados mesmo com o elemento bloqueado.
                </p>
              )}
            </>
          ) : (
            <div className="lt-panel-section">
              <Field label="Área jurídica">
                <select
                  value={model.area}
                  onChange={(event) =>
                    commit({ ...model, area: event.target.value })
                  }
                >
                  {areas.map((area) => (
                    <option key={area.key} value={area.key}>
                      {area.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Descrição">
                <textarea
                  rows={4}
                  maxLength={1000}
                  value={model.description || ''}
                  onChange={(event) =>
                    commit({ ...model, description: event.target.value })
                  }
                  placeholder="Quando este modelo deve ser utilizado?"
                />
              </Field>
              <Field label="Guia de margem (mm)">
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={model.document.marginMm}
                  onChange={(event) =>
                    changeDocument({
                      ...model.document,
                      marginMm: Math.max(
                        0,
                        Math.min(50, Number(event.target.value) || 0),
                      ),
                    })
                  }
                />
              </Field>
              <div className="lt-format-card">
                <FileText size={25} />
                <strong>A4 · Retrato</strong>
                <span>210 × 297 mm</span>
                <p>
                  A prévia e o PDF usam as posições da página. O Word mantém
                  textos e imagens editáveis.
                </p>
              </div>
              {!preview && (
                <p className="lt-hint">
                  Selecione um elemento na página para ajustar suas
                  propriedades.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>
    </section>
  )
}
